import React, { useEffect, useRef, useState } from 'react';
import { Chart } from 'primereact/chart';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import {
  getCourseCountByRole,
  getCourseDetails,
  getCourseForRoles,
  getCourseStatusCounts
} from '../../services/DashboardService';
import '../Dashboard/Dashboard.scss';

const initialBarData = {
  labels: [],
  datasets: [{ label: 'User Count', backgroundColor: [], data: [] }],
};
const initialDonutData = {
  labels: ['Published', 'Unpublished'],
  datasets: [{
    data: [0, 0],
    backgroundColor: ['#42A5F5', '#FFA726'],
    hoverBackgroundColor: ['#64B5F6', '#FFB74D'],
  }],
};
const barOptions = {
  indexAxis: 'x',
  responsive: true,
  plugins: { legend: { display: false } },
  scales: {
    x: { ticks: { font: { weight: 'bold' } }, grid: { display: false } },
    y: { grid: { display: false } },
  },
};
const donutOptions = { plugins: { legend: { position: 'top' } } };
const Dashboard = () => {
  const chartRef = useRef(null);
  const donutChartRef = useRef(null);
  const [barData, setBarData] = useState(initialBarData);
  const [donutData, setDonutData] = useState(initialDonutData);
  const [tableData, setTableData] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [filterType, setFilterType] = useState(null);
  const [showTable, setShowTable] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchCourseCountByRole = async () => {
    try {
      const response = await getCourseCountByRole();
      const filtered = response.filter(({ role }) => role !== 'BCM');
      const labels = filtered.map(({ role }) => role);
      const data = filtered.map(({ courseCount }) => courseCount);
      const colors = ['#42A5F5', '#66BB6A', '#FFA726', '#AB47BC', '#FF7043'];
      setBarData({
        labels,
        datasets: [{ label: 'Course Count', backgroundColor: colors.slice(0, data.length), data }],
      });
    } catch (err) {
      console.error('Bar chart error:', err);
    }
  };
  const fetchCourseStatusCounts = async () => {
    try {
      const counts = await getCourseStatusCounts();
      setDonutData((prev) => ({
        ...prev,
        datasets: [{ ...prev.datasets[0], data: [counts.Published || 0, counts.Unpublished || 0] }],
      }));
    } catch (err) {
      console.error('Donut chart error:', err);
    }
  };
  const handleDonutClick = async ({ nativeEvent }) => {
  const chart = donutChartRef.current?.getChart();
  const [point] = chart?.getElementsAtEventForMode(nativeEvent, 'nearest', { intersect: true }, false) || [];
  if (!point) return;
  const status = donutData.labels[point.index];
  setFilterType('status');
  setSelectedFilter(status);
  try {
    const allCourses = await getCourseDetails();
    const filteredCourses = allCourses.filter(course => {
      if (status === 'Published') return course.is_published === true;
      if (status === 'Unpublished') return course.is_published === false;
      return false;
    });
    setTableData(filteredCourses);
  } catch (err) {
    console.error('Donut click error:', err);
    setTableData([]);
  } finally {
    setShowTable(true);
  }
};

  const handleBarClick = async (_e, elements) => {
  if (!elements?.length) return;
  const index = elements[0].index;
  const role = barData.labels[index];
  const roleKey = role.toLowerCase();
  setFilterType('role');
  setSelectedFilter(role);
  try {
    const validRoles = ['asha', 'anm', 'af'];
    if (!validRoles.includes(roleKey)) throw new Error('Invalid role');
    const data = await getCourseForRoles();
    const courses = data[roleKey] || [];
    const filteredCourses = courses
      .filter(course => course.roleNames?.includes(role))
      .map(course => ({
        ...course,
        roleNames: [role],
      }));
    setTableData(filteredCourses);
  } catch (err) {
    console.error('Bar click error:', err);
    setTableData([]);
  } finally {
    setShowTable(true);
  }
};
  useEffect(() => {
    fetchCourseCountByRole();
    fetchCourseStatusCounts();
  }, []);

  return (
    <div className="dashboard-container">
      <div className="dashboard-content">
        <div className="charts-row">
          <div className="chart-card bar-chart">
            <h4 className="chart-title">No.Of Courses For the user</h4>
            <Chart type="bar" data={barData} options={{ ...barOptions, onClick: handleBarClick }} ref={chartRef} />
          </div>
          <div className="chart-card pie-chart">
            <h4 className="chart-title">Total Courses</h4>
            <Chart type="doughnut" data={donutData} options={donutOptions} ref={donutChartRef} onClick={handleDonutClick} />
          </div>
        </div>
        {showTable && (
          <div className="table-container">
            <h4 className="chart-title">
              Courses - {filterType === 'status' ? selectedFilter : `Role: ${selectedFilter}`}
            </h4>
            <DataTable
              value={tableData}
              paginator
              rows={rowsPerPage}
              first={currentPage * rowsPerPage}
              onPage={({ first, rows }) => {
                setCurrentPage(first / rows);
                setRowsPerPage(rows);
              }}
              scrollable
              scrollHeight="510px"
              rowsPerPageOptions={[5, 10, 20, 50]}
              emptyMessage="No data found."
              className="table-viewport custom-tooltip"
              resizableColumns
              size="small"
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
              currentPageReportTemplate="{first} to {last} of {totalRecords}"
            >
              <Column field="title" header="Courses" />
              <Column field="roleNames" header="Audience" body={(rowData) => rowData.roleNames?.join(', ')} />
            </DataTable>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;